// =============================================
// Owner / entity resolution (shared by bookkeeping tabs)
// =============================================
// An "owner entity" is one set of books: the account owner, a family member, a
// business, or — under Client's Accounts — the client / their family / their
// businesses. These helpers mirror the (module-private) logic in ProcessReceiptsTab
// so the Journal Entries + GL Report tabs scope COA accounts and entries the same
// way. Identity lives in `users` / `user_businesses`; comparisons use `==` because
// ids round-trip through localStorage as strings.
// =============================================

export interface OwnerEntity {
  owner_type: string;
  owner_id: number;
  display_name: string;
  badge: string;
}

export interface CurrentUserLite {
  user_id: number;
  owner_id: number | null;
  first_name: string;
  last_name: string;
}

export function getCurrentUser(usersData: Record<string, any>[]): CurrentUserLite | null {
  try {
    const raw = localStorage.getItem('akt_session_user');
    if (!raw) return null;
    const session = JSON.parse(raw);
    const sessionId = Number(session.id || session.user_id);
    if (!sessionId) return null;
    const userRow = usersData.find((u: any) => Number(u.user_id) === sessionId);
    if (!userRow) return null;
    return {
      user_id: sessionId,
      owner_id: userRow.owner_id == null || userRow.owner_id === '' ? null : Number(userRow.owner_id),
      first_name: userRow.first_name || '',
      last_name: userRow.last_name || '',
    };
  } catch { /* ignore */ }
  return null;
}

// The account owner is the LOGGED-IN user's account: themselves, or their owner for a
// sub-user. Never "the first owner_id==null row" — with more than one owner in `users`
// that resolves to another tenant (wrong name shown, edits landing on the wrong row).
// First-owner is kept only as the no-session / row-not-cached fallback.
export function getAccountOwner(usersData: Record<string, any>[]): Record<string, any> | null {
  const me = getCurrentUser(usersData);
  const mine = me ? getAccountOwnerForUser(me.user_id, usersData) : null;
  if (mine) return mine;
  return usersData.find((u: any) => u.owner_id == null || u.owner_id === '') || usersData[0] || null;
}

export function getAccountOwnerId(usersData: Record<string, any>[]): number | null {
  const owner = getAccountOwner(usersData);
  return owner ? Number(owner.user_id) : null;
}

export function getAccountOwnerForUser(userId: number, usersData: Record<string, any>[]): Record<string, any> | null {
  // eslint-disable-next-line eqeqeq
  const user = usersData.find((u: any) => u.user_id == userId);
  if (!user) return null;
  if (user.owner_id == null || user.owner_id === '') return user;
  // eslint-disable-next-line eqeqeq
  return usersData.find((u: any) => u.user_id == user.owner_id) || null;
}

// The account owner's own books: owner + family members + own businesses.
export function getOwnerEntities(accountOwnerId: number, usersData: Record<string, any>[], businessesData: Record<string, any>[]): OwnerEntity[] {
  const entities: OwnerEntity[] = [];
  // eslint-disable-next-line eqeqeq
  const owner = usersData.find((u: any) => u.user_id == accountOwnerId);
  if (owner) entities.push({ owner_type: 'account_owner', owner_id: Number(owner.user_id), display_name: `${owner.first_name || ''} ${owner.last_name || ''}`.trim() || owner.email || 'Account Owner', badge: 'Owner' });
  usersData
    // eslint-disable-next-line eqeqeq
    .filter((u: any) => u.owner_id == accountOwnerId && u.is_deleted !== 1 && u.relationship !== 'Personal Client')
    .forEach((u: any) => entities.push({ owner_type: 'family_member', owner_id: Number(u.user_id), display_name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || 'Family Member', badge: 'Family' }));
  businessesData
    // eslint-disable-next-line eqeqeq
    .filter((b: any) => b.owner_id == accountOwnerId && b.is_deleted !== 1 && b.is_client_business !== 1)
    .forEach((b: any) => entities.push({ owner_type: 'business', owner_id: Number(b.business_id), display_name: b.busi_legal_name || b.busi_operating_name || 'Business', badge: 'Business' }));
  return entities;
}

// The selected client's books: the client, their family, their businesses.
export function getClientEntities(clientId: number | string, usersData: Record<string, any>[], businessesData: Record<string, any>[]): OwnerEntity[] {
  const entities: OwnerEntity[] = [];
  // eslint-disable-next-line eqeqeq
  const client = usersData.find((u: any) => u.user_id == clientId);
  const clientName = client ? (`${client.first_name || ''} ${client.last_name || ''}`.trim() || client.email || 'Client') : 'Client';
  if (client) entities.push({ owner_type: 'client_personal', owner_id: Number(client.user_id), display_name: clientName, badge: 'Client' });
  usersData
    // eslint-disable-next-line eqeqeq
    .filter((u: any) => u.owner_id == clientId && u.is_deleted !== 1 && u.relationship !== 'Personal Client')
    .forEach((u: any) => entities.push({ owner_type: 'client_personal', owner_id: Number(u.user_id), display_name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || 'Family', badge: `${clientName}'s Family` }));
  businessesData
    // eslint-disable-next-line eqeqeq
    .filter((b: any) => b.owner_id == clientId && b.is_deleted !== 1)
    .forEach((b: any) => entities.push({ owner_type: 'client_business', owner_id: Number(b.business_id), display_name: b.busi_legal_name || b.busi_operating_name || 'Business', badge: `${clientName}'s Biz` }));
  return entities;
}

// Resolve the entity list for a pillar scope + (optional) selected client.
export function resolveEntities(
  scope: 'owner' | 'clients',
  accountOwnerId: number,
  selectedClientId: number | string | null,
  usersData: Record<string, any>[],
  businessesData: Record<string, any>[],
): OwnerEntity[] {
  if (!accountOwnerId) return [];
  if (scope === 'clients') {
    if (selectedClientId == null) return [];
    return getClientEntities(selectedClientId, usersData, businessesData);
  }
  return getOwnerEntities(accountOwnerId, usersData, businessesData);
}
